import { PDFPostData } from '@blk/aw-bff-typescript-models';
import { AxiosResponse } from 'axios';
import { EXTENDED_API_TIMEOUT } from '../../APIConstants';
import { AGraphWrappedType, unwrapAGraphResponse, wrapAGraphRequest } from '../../utils/AGraphWrapper';
import { nonBlockingGzip } from '../../utils/CompressionUtils';
import { isAGraphEnabled } from './AGraphUtils';
import { awBffApi } from './Base';

type TemplatesResponse = Promise<AxiosResponse<{ [key: string]: string }>>;
export type GetTemplatesType = (templateNames: string[]) => TemplatesResponse;

export async function getTemplates(templateNames: string[]): Promise<AxiosResponse<{ [key: string]: string }>> {
  const path = `pdf-export/templates`;
  const response = await awBffApi.post<AGraphWrappedType<{ [key: string]: string }, 'templates'>>(
    path,
    wrapAGraphRequest(templateNames, 'templateNames', isAGraphEnabled()),
    {
      timeout: EXTENDED_API_TIMEOUT,
    }
  );
  return unwrapAGraphResponse(response, 'templates');
}

function convertBase64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

export async function postGeneratePdf(
  pdfPostData: PDFPostData,
  compressNetworkRequests: boolean = false,
  pkg: string
): Promise<AxiosResponse<ArrayBuffer>> {
  const path = `pdf-export/generate-pdf`;
  const pdfRequestHeaders: { [key: string]: string } = {
    Accept: 'application/json, text/plain, application/pdf',
  };
  const isAGraph = isAGraphEnabled();

  if (compressNetworkRequests) {
    pdfRequestHeaders['Content-Encoding'] = 'gzip';
    pdfRequestHeaders['Content-Type'] = 'application/json;charset=UTF-8';
    return nonBlockingGzip(JSON.stringify(pdfPostData)).then(async (payload) => {
      const response = await awBffApi.post<AGraphWrappedType<ArrayBuffer | string, 'data'>>(path, payload, {
        responseType: isAGraph ? 'json' : 'arraybuffer',
        headers: pdfRequestHeaders,
        timeout: EXTENDED_API_TIMEOUT,
        params: { pkg },
      });
      const unwrapped = unwrapAGraphResponse(response, 'data');

      if (isAGraph && typeof unwrapped.data === 'string') {
        return {
          ...unwrapped,
          data: convertBase64ToArrayBuffer(unwrapped.data),
        };
      }

      return unwrapped as AxiosResponse<ArrayBuffer>;
    });
  }
  const response = await awBffApi.post<AGraphWrappedType<ArrayBuffer | string, 'data'>>(path, pdfPostData, {
    responseType: isAGraph ? 'json' : 'arraybuffer',
    headers: pdfRequestHeaders,
    timeout: EXTENDED_API_TIMEOUT,
    params: { pkg },
  });
  const unwrapped = unwrapAGraphResponse(response, 'data');

  if (isAGraph && typeof unwrapped.data === 'string') {
    return {
      ...unwrapped,
      data: convertBase64ToArrayBuffer(unwrapped.data),
    };
  }

  return unwrapped as AxiosResponse<ArrayBuffer>;
}

export async function postArchivePdf(
  blob: Blob,
  internal: boolean,
  customFileName: string,
  compressNetworkRequests: boolean = false,
  batchReport: boolean = false,
  portfolioID: string | undefined = undefined,
  analysisDate: string | undefined = undefined
): Promise<AxiosResponse<string>> {
  const path = `pdf-export/archive-pdf`;
  const pdfRequestHeaders: { [key: string]: string } = {
    Accept: 'application/json, text/plain, application/pdf',
  };

  const formData = new FormData();
  formData.append('file', blob, customFileName);
  formData.append('internal', internal.toString());
  formData.append('customFilename', customFileName);
  formData.append('batchReport', batchReport.toString());
  if (portfolioID) {
    formData.append('portfolioID', portfolioID);
  }
  if (analysisDate) {
    formData.append('analysisDate', analysisDate);
  }

  if (compressNetworkRequests) {
    pdfRequestHeaders['Content-Encoding'] = 'gzip';
    return nonBlockingGzip(JSON.stringify(formData)).then((gzippedPayload) =>
      awBffApi.post<string>(path, gzippedPayload, {
        headers: pdfRequestHeaders,
        timeout: EXTENDED_API_TIMEOUT,
        params: {},
      })
    );
  }

  return awBffApi.post<string>(path, formData, {
    headers: pdfRequestHeaders,
    timeout: EXTENDED_API_TIMEOUT,
    params: {},
  });
}
